import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, List, ListItem, Typography, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SkusActions } from 'state/modules/skus';
import { withRouter } from 'react-router-dom';
import { UpdateAccounting } from 'components/Modals/Skus';
import { Card } from 'components/UI';
import i18n from 'i18n-js';

const SkuList = styled(List)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  width: '100%',
  paddingTop: 0,
  marginTop: -5,
  alignItems: 'flex-start',
});

const SkuListItem = styled(ListItem)(({ theme }) => ({
  width: '100%',
  padding: '0 4px 8px 0',
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
}));

const ListTextPrimary = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
  fontWeight: 500,
  wordBreak: 'break-word',
}));

const TextRight = styled(Box)({
  textAlign: 'right',
});

const SkuAccounting = ({ id }) => {
  const dispatch = useDispatch();
  const { sku } = useSelector((state) => state.skus);
  const [openAccountingModal, setOpenAccountingModal] = useState(false);
  const taxesEnabled = useSelector((state) => state.config.settings?.core?.receivable?.taxes);
  const editable = !sku?.frozen_time;

  useEffect(() => {
    if (id) {
      dispatch(SkusActions.fetchSkuById(id));
    }
  }, [id]); // eslint-disable-line

  /**
   * Parse sku data
   * @param {object} sku Object with sku data
   */
  const getSkuData = (sku = {}) => {
    const skuInfo = [
      {
        label: `${i18n.t(`skus.sku_accounting`)}:`,
        value: sku?.accounting_type?.is_revenue
          ? i18n.t(`skus.wizard.revenue_label`)
          : sku?.accounting_type?._type === 'EXPENSE' && !sku?.accounting_type?.is_revenue
            ? i18n.t(`skus.wizard.discount_label`)
            : i18n.t(`skus.wizard.hold_label`),
      },
      { label: `${i18n.t(`skus.account_type`)}:`, value: sku?.accounting_type?.name || '-' },
    ];

    if (taxesEnabled !== false) {
      const taxes = [
        { label: `${i18n.t(`skus.tax_type`)}:`, value: sku?.taxing_choice || '-' },
        {
          label: `${i18n.t(`skus.default_tax_rate`)}:`,
          value: sku?.tax_rate_default ? `${sku?.tax_rate_default}` : '-',
        },
      ];
      skuInfo.push(...taxes);
    }

    return skuInfo;
  };

  const handleOpenEditAccountingModal = () => {
    setOpenAccountingModal((prevState) => !prevState);
  };
  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('skus.edit_accounting'),
      onClick: handleOpenEditAccountingModal,
    },
  ];

  return (
    <Card
      menuItems={editable ? items : undefined}
      menuPermissions={{
        action: 'update',
        subject: 'invoice_item_type',
        type: 'can',
      }}
      title={i18n.t('skus.accounting')}
      headerActionType="vertical"
    >
      <Box>
        <SkuList>
          {getSkuData(sku).map((val, index) => (
            <SkuListItem key={index}>
              <ListItemText
                sx={{ display: 'flex', justifyContent: 'space-between' }}
                primary={<ListTextPrimary>{val.label}</ListTextPrimary>}
                secondary={
                  <TextRight>
                    <Typography variant="body2" component="span" color="text.secondary">
                      {val.value}
                    </Typography>
                  </TextRight>
                }
              />
            </SkuListItem>
          ))}
        </SkuList>
      </Box>
      {openAccountingModal && (
        <UpdateAccounting
          id={id}
          open={openAccountingModal}
          handleClick={handleOpenEditAccountingModal}
        />
      )}
    </Card>
  );
};

SkuAccounting.propTypes = {
  id: PropTypes.any,
};

export default withRouter(SkuAccounting);
